<template>
  <div class="min-h-screen bg-neutral-50 flex">
    <!-- Sidebar -->
    <aside 
      :class="[
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-neutral-900 text-white transition-transform duration-300',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        'w-64'
      ]"
    >
      <!-- Logo Bereich -->
      <div class="h-16 flex items-center gap-2 px-6 border-b border-neutral-700 overflow-hidden">
        <h1 class="text-xl font-bold tracking-tight flex-shrink-0">studioPRO</h1>
        <span class="ml-2 text-xs text-neutral-400 border border-neutral-600 px-1.5 py-0.5 rounded flex-shrink-0">
          {{ userRole }}
        </span>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <!-- Dozenten/Schüler sehen nur ihr eigenes Dashboard + Profil, nicht
             die komplette Admin-Navigation (Finanzen, Stammdaten, etc.) -
             passend dazu sind die zugehörigen Backend-Routen inzwischen
             admin-only (siehe backend/src/routes/*.ts). -->
        <template v-if="authStore.isTeacher">
          <router-link
            to="/dashboard/teacher"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/dashboard/teacher') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Mein Dashboard
          </router-link>
          <router-link
            to="/my-calendar"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/my-calendar') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Kalender
          </router-link>

          <router-link
            to="/my-courses"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/my-courses') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Meine Kurse
          </router-link>
          <router-link
            to="/practice"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/practice') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
            </svg>
            Übungsmaterial
          </router-link>
          <router-link
            to="/practice-offline"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/practice-offline') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
            </svg>
            Üben &amp; Metronom
          </router-link>
          <router-link
            to="/availability"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/availability') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Verfügbarkeiten
          </router-link>
          <router-link
            to="/messages"
            :class="[
              'flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/messages') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <span class="flex items-center">
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Nachrichten
            </span>
            <span v-if="messageStore.unreadTotal > 0" class="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center">
              {{ messageStore.unreadTotal }}
            </span>
          </router-link>
        </template>
        <template v-else-if="authStore.isStudent">
          <router-link
            to="/dashboard/student"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/dashboard/student') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Mein Dashboard
          </router-link>
          <router-link
            to="/practice-offline"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/practice-offline') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
            </svg>
            Üben &amp; Metronom
          </router-link>
          <router-link
            to="/messages"
            :class="[
              'flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/messages') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <span class="flex items-center">
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Nachrichten
            </span>
            <span v-if="messageStore.unreadTotal > 0" class="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center">
              {{ messageStore.unreadTotal }}
            </span>
          </router-link>
          <router-link
            to="/my-availability"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/my-availability') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Meine Verfügbarkeit
          </router-link>
        </template>
        <template v-else-if="authStore.isGuardian">
          <router-link
            to="/dashboard/guardian"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/dashboard/guardian') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Familienbereich
          </router-link>
          <router-link
            to="/practice-offline"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/practice-offline') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
            </svg>
            Üben &amp; Metronom
          </router-link>
          <router-link
            to="/messages"
            :class="[
              'flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/messages') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <span class="flex items-center">
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Nachrichten
            </span>
            <span v-if="messageStore.unreadTotal > 0" class="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center">
              {{ messageStore.unreadTotal }}
            </span>
          </router-link>
          <router-link
            to="/my-availability"
            :class="[
              'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
              isActive('/my-availability') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
            ]"
            @click="closeSidebarOnMobile"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Verfügbarkeit
          </router-link>
        </template>
        <template v-else>
        <!-- Hauptseite -->
        <router-link
          to="/"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Dashboard
        </router-link>

        <router-link
          to="/messages"
          :class="[
            'flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/messages') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <span class="flex items-center">
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Nachrichten
          </span>
          <span v-if="messageStore.unreadTotal > 0" class="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center">
            {{ messageStore.unreadTotal }}
          </span>
        </router-link>

        <!-- Tagesgeschäft Section -->
        <div class="pt-4 pb-2">
          <p class="px-3 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Tagesgeschäft</p>
        </div>


        <router-link
          to="/ferienkalender"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/ferienkalender') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Ferienkalender
        </router-link>

        <router-link
          to="/lesson-slots"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/lesson-slots') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Unterrichtstermine
        </router-link>

        <router-link
          to="/courses"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/courses') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Kurse
        </router-link>

        <router-link
          to="/schedule-drafts"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/schedule-drafts') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Automatischer Stundenplan
        </router-link>

        <router-link
          to="/availability-survey"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/availability-survey') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Verfügbarkeits-Abfrage
        </router-link>

        <router-link
          to="/admin-calendar"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/admin-calendar') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Admin-Kalender
        </router-link>

        <router-link
          to="/teacher-schedules"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/teacher-schedules') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Verfügbarkeiten &amp; Abwesenheiten
        </router-link>

        <router-link
          to="/cancelled-lessons"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/cancelled-lessons') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          Ausgefallene Stunden
        </router-link>

        <router-link
          to="/probestunden"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/probestunden') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Probestunden
        </router-link>

        <router-link
          to="/wiedervorlagen"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/wiedervorlagen') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          Wiedervorlagen
        </router-link>

        <!-- Verwaltung Section -->
        <div class="pt-4 pb-2">
          <p class="px-3 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Verwaltung</p>
        </div>

        <router-link
          to="/students"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/students') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Schüler
        </router-link>

        <router-link
          to="/teachers"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/teachers') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Lehrer
        </router-link>

        <router-link
          to="/persons"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/persons') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Personen
        </router-link>

        <router-link
          to="/subjects"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/subjects') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Fächer
        </router-link>

        <router-link
          to="/rooms"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/rooms') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
          Raumverwaltung
        </router-link>

        <!-- Finanzen Section (echte Module: Verträge, Karten, Abrechnung) -->
        <div class="pt-4 pb-2">
          <p class="px-3 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Finanzen</p>
        </div>

        <router-link
          to="/contracts"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/contracts') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Verträge
        </router-link>

        <router-link
          to="/cards"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/cards') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          Karten
        </router-link>

        <router-link
          to="/billing"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/billing') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Abrechnung
        </router-link>

        <router-link
          to="/teacher-pay"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/teacher-pay') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Honorarabrechnung
        </router-link>

        <!-- Einstellungen Section -->
        <div class="pt-4 pb-2">
          <p class="px-3 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Einstellungen</p>
        </div>

        <router-link
          to="/settings"
          :class="[
            'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors',
            isActive('/settings') ? 'bg-neutral-800 text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
          ]"
          @click="closeSidebarOnMobile"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Einstellungen
        </router-link>

        </template>
      </nav>

      <!-- User Info -->
      <div class="p-4 border-t border-neutral-700">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-neutral-700 flex items-center justify-center text-sm font-medium">
            {{ userInitials }}
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium truncate">{{ userName }}</p>
            <p class="text-xs text-neutral-400 truncate">{{ userRole }}</p>
          </div>
        </div>
      </div>
    </aside>

    <!-- Sidebar Overlay (Desktop + Mobile) -->
    <div 
      v-if="sidebarOpen" 
      class="fixed inset-0 bg-neutral-900/50 z-40"
      @click="sidebarOpen = false"
    />

    <!-- Main Content -->
    <div 
      class="flex-1 flex flex-col min-w-0 min-h-0"
      :class="sidebarOpen ? 'lg:ml-64' : 'ml-0'"
    >
      <!-- Header -->
      <header class="h-16 bg-white border-b border-neutral-200 flex items-center justify-between px-4 lg:px-6">
        <!-- Sidebar Toggle Button -->
        <button 
          class="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
          @click="sidebarOpen = !sidebarOpen"
          title="Navigation ein-/ausblenden"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <!-- Breadcrumbs -->
        <div class="flex items-center text-sm">
          <span class="text-neutral-500">studioPRO</span>
        </div>

        <!-- Header Actions -->
        <div class="flex items-center gap-4">
          <!-- Split Screen Controls - nur Admin: die geteilte Ansicht löst die
               Panel-Komponenten per router.resolve auf (echte Seiten, kuratierte
               Admin-Liste) statt über <router-view> und umgeht damit die
               Rollen-Guards des Routers. Für Dozenten/Schüler bleibt es deshalb
               bei der Einzelansicht (echter router-view, korrekt abgesichert). -->
          <div v-if="authStore.isAdmin" class="flex items-center gap-1">
            <!-- Single View Button -->
            <button
              :class="[
                'p-2 rounded-md transition-all',
                currentSplitMode === 'single'
                  ? 'bg-neutral-200 text-neutral-900'
                  : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
              ]"
              @click="setSplitMode('single')"
              title="Einzelansicht"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke-width="2"/>
              </svg>
            </button>

            <!-- Split View Button -->
            <button
              :class="[
                'p-2 rounded-md transition-all',
                currentSplitMode === 'dual'
                  ? 'bg-neutral-200 text-neutral-900'
                  : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
              ]"
              @click="setSplitMode('dual')"
              title="Geteilte Ansicht"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke-width="2"/>
                <line x1="12" y1="3" x2="12" y2="21" stroke-width="2"/>
              </svg>
            </button>
          </div>

          <!-- External Window Button -->
          <button 
            v-if="currentSplitMode === 'single'"
            class="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
            @click="openExternalWindow"
            title="Dashboard in neuem Fenster öffnen"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
          
          <!-- Aktuelles Datum -->
          <span class="text-sm text-neutral-500 hidden sm:block">
            {{ formattedDate }}
          </span>
          
          <button
            class="text-sm text-neutral-600 hover:text-neutral-900 font-medium"
            @click="showChangePassword = true"
          >
            Passwort ändern
          </button>

          <!-- Logout Button -->
          <button 
            class="text-sm text-neutral-600 hover:text-neutral-900 font-medium"
            @click="handleLogout"
          >
            Abmelden
          </button>
        </div>
      </header>

      <!-- Page Content with Split Screen -->
      <main class="flex-1 overflow-y-auto p-4 lg:p-6" style="min-height: 0;">
        <!-- Normal Mode (Single View) -->
        <div v-if="currentSplitMode === 'single'" class="h-full">
          <router-view />
        </div>

        <!-- Split Screen Mode -->
        <div v-else :class="['grid gap-4 h-full p-2', splitGridClass]">
          <!-- Panel 1 -->
          <div class="bg-white rounded-lg border border-neutral-200 overflow-hidden">
            <div class="px-4 py-2 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between">
              <select 
                v-model="panel1Route"
                class="text-sm border-0 bg-transparent font-medium text-neutral-900 focus:ring-0 cursor-pointer"
              >
                <option v-for="option in routeOptions" :key="option.path" :value="option.path">
                  {{ option.name }}
                </option>
              </select>
              <button 
                v-if="panel1Route"
                @click="navigateToPanel1"
                class="text-xs text-neutral-500 hover:text-neutral-700"
              >
                Oeffnen
              </button>
            </div>
            <div class="h-[calc(100%-40px)] overflow-y-auto p-2">
              <component 
                :is="getComponentForRoute(panel1Route)" 
                v-if="getComponentForRoute(panel1Route)" 
              />
              <router-view v-else :key="panel1Route" />
            </div>
          </div>

          <!-- Panel 2 -->
          <div v-if="currentSplitMode !== 'single'" class="bg-white rounded-lg border border-neutral-200 overflow-hidden">
            <div class="px-4 py-2 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between">
              <select 
                v-model="panel2Route"
                class="text-sm border-0 bg-transparent font-medium text-neutral-900 focus:ring-0 cursor-pointer"
              >
                <option v-for="option in routeOptions" :key="option.path" :value="option.path">
                  {{ option.name }}
                </option>
              </select>
              <button 
                v-if="panel2Route"
                @click="navigateToPanel2"
                class="text-xs text-neutral-500 hover:text-neutral-700"
              >
                Oeffnen
              </button>
            </div>
            <div class="h-[calc(100%-40px)] overflow-y-auto p-2">
              <component 
                :is="getComponentForRoute(panel2Route)" 
                v-if="getComponentForRoute(panel2Route)"
              />
              <div v-else class="p-4 text-center text-neutral-400">
                <p>{{ getRouteName(panel2Route) }}</p>
                <p class="text-sm mt-2">Diese Seite ist in der geteilten Ansicht nicht verfügbar.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
    <MessageToast />
    <ChangePasswordModal v-if="showChangePassword" @close="showChangePassword = false" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, defineAsyncComponent, type Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useMessageStore } from '@/modules/messages/stores/message.store'
import MessageToast from '@/modules/messages/components/MessageToast.vue'
import ChangePasswordModal from '@/modules/auth/components/ChangePasswordModal.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const messageStore = useMessageStore()

// State
const sidebarOpen = ref(false)
// Echter Name der verknüpften Lehrkraft/Schüler/Erziehungsberechtigten
// (displayName, vom Backend aus Teacher/Student/Guardian aufgelöst - das
// Konto selbst trägt keinen Namen). Ohne Verknüpfung (reine Verwaltungskonten)
// bleibt displayName null - dann der E-Mail-Teil vor dem @, ehrlicher als ein
// erfundener Platzhalter.
const userName = computed(() => authStore.user?.displayName || authStore.user?.email?.split('@')[0] || 'Nutzer')
const showChangePassword = ref(false)
const userRoleLabels: Record<string, string> = { admin: 'Admin', teacher: 'Dozent', student: 'Schüler', guardian: 'Eltern' }
const userRole = computed(() => userRoleLabels[authStore.user?.role ?? ''] || '')
const currentSplitMode = ref('single')
const panel1Route = ref(route.path)
const panel2Route = ref('/messages')

// Geteilte Ansicht: die Panels rendern die ECHTEN Router-Komponenten (per
// router.resolve aufgelöst) statt einer eigenen Komponentenliste - früher zeigte
// sie hier eine hartcodierte Map auf alte Fake-Prototyp-Seiten (src/views/*).
// Ausgewählt werden nur echte Admin-Seiten; Finanzen/Stundenkonto/Berichte
// fehlen bewusst, bis diese Module (Roadmap) echt angebunden sind.
const SPLIT_PANEL_PATHS = [
  '/',
  '/messages',
  '/admin-calendar',
  '/lesson-slots',
  '/probestunden',
  '/students',
  '/teachers',
  '/rooms',
  '/subjects',
  '/schedule-drafts',
  '/wiedervorlagen',
  '/cancelled-lessons',
  '/teacher-schedules',
  '/settings'
]

function routeTitle(path: string): string {
  return (router.resolve(path).meta.title as string | undefined) || path
}

// Panel 1 folgt immer der aktuellen Route (auch z.B. /students/:id) - steht sie
// nicht in der Liste, wird sie als zusätzliche Option angehängt, damit das
// Dropdown nicht leer bleibt.
const routeOptions = computed(() => {
  const paths = SPLIT_PANEL_PATHS.includes(panel1Route.value) ? SPLIT_PANEL_PATHS : [panel1Route.value, ...SPLIT_PANEL_PATHS]
  return paths.map((path) => ({ path, name: routeTitle(path) }))
})

const panelComponentCache = new Map<string, Component | null>()

// Computed
const userInitials = computed(() => {
  const names = userName.value.split(' ')
  return names.map(n => n[0]).join('').toUpperCase().slice(0, 2)
})

const formattedDate = computed(() => {
  return new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
})

const splitGridClass = computed(() => {
  if (currentSplitMode.value === 'dual') return 'grid-cols-2'
  return 'grid-cols-1'
})

// Methods
function isActive(path: string): boolean {
  if (path === '/') {
    return route.path === '/'
  }
  return route.path.startsWith(path)
}

function closeSidebarOnMobile() {
  if (window.innerWidth < 1024) {
    sidebarOpen.value = false
  }
}

function setSplitMode(mode: string) {
  currentSplitMode.value = mode
  try {
    localStorage.setItem('split_mode', mode)
  } catch (e) {
    console.error('Error saving split mode:', e)
  }
}

function openExternalWindow() {
  // Open current route in new window
  const currentUrl = window.location.href
  window.open(currentUrl, '_blank', 'width=1200,height=800,scrollbars=yes')
}

function getRouteName(path: string): string {
  return routeTitle(path)
}

/** Löst die echte Router-Komponente zum Pfad auf (Lazy-Loader werden in eine
 *  Async-Komponente gewickelt, Ergebnis gecacht). null = kein echter Treffer
 *  (unbekannter Pfad -> Catch-all-404-Route). */
function getComponentForRoute(path: string): Component | null {
  if (panelComponentCache.has(path)) return panelComponentCache.get(path) ?? null
  const resolved = router.resolve(path)
  const record = resolved.matched[resolved.matched.length - 1]
  const raw = record?.components?.default as Component | (() => Promise<unknown>) | undefined
  const isNotFound = resolved.matched.some((r) => r.path.includes(':pathMatch'))
  let component: Component | null = null
  if (raw && !isNotFound) {
    // Router-Einträge sind hier durchgängig Lazy-Imports (() => import(...)).
    component = typeof raw === 'function' && !('__vccOpts' in raw) ? defineAsyncComponent(raw as () => Promise<Component>) : (raw as Component)
  }
  panelComponentCache.set(path, component)
  return component
}

function navigateToPanel1() {
  if (panel1Route.value) {
    router.push(panel1Route.value)
  }
}

function navigateToPanel2() {
  if (panel2Route.value) {
    router.push(panel2Route.value)
  }
}

async function handleLogout() {
  await authStore.logout()
  router.push('/login')
}

// Initialize split mode and sidebar state from localStorage
onMounted(() => {
  try {
    // Nur Admins dürfen die geteilte Ansicht nutzen (siehe Kommentar bei den
    // Split-Screen-Controls oben) - ein aus einer früheren Admin-Sitzung im
    // selben Browser gespeicherter "dual"-Wert darf für Dozenten/Schüler
    // nicht greifen.
    const savedSplitMode = localStorage.getItem('split_mode')
    if (savedSplitMode && authStore.isAdmin) {
      currentSplitMode.value = savedSplitMode
    }

    const savedSidebar = localStorage.getItem('sidebar_open')
    if (savedSidebar !== null) {
      sidebarOpen.value = savedSidebar === 'true'
    }
  } catch (e) {
    console.error('Error loading user preferences:', e)
  }
})

// Ungelesen-Badge fürs Sidebar - Polling statt WebSocket/SSE (im Projekt
// bisher nirgends vorhanden, siehe messages-Modul-Plan).
onMounted(() => {
  if (authStore.isAuthenticated) messageStore.startUnreadPolling()
})
onUnmounted(() => {
  messageStore.stopUnreadPolling()
})

// Save sidebar state to localStorage when it changes
watch(sidebarOpen, (newValue) => {
  try {
    localStorage.setItem('sidebar_open', String(newValue))
  } catch (e) {
    console.error('Error saving sidebar state:', e)
  }
})

// Watch for route changes to update panel1
watch(() => route.path, (newPath) => {
  panel1Route.value = newPath
})
</script>
